// Formatting for the billing pages. Money arrives from the API as integer
// cents and stays that way until it is drawn; everything is US dollars.

const currency = (options) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', ...options });
const CENTS = currency();
const DOLLARS = currency({ maximumFractionDigits: 0, minimumFractionDigits: 0 });

export const formatMoney = (cents) => CENTS.format((cents || 0) / 100);

// Whole dollars, for headline figures where cents are noise.
export const formatDollars = (cents) => DOLLARS.format(Math.round((cents || 0) / 100));

// Axis ticks and tight labels: $950, $9,739, $48.2K, $1.2M. ``always`` compacts
// small amounts too, so every tick on an axis that reaches $10K reads alike
// ($0, $5K, $10K rather than $0, $5,000, $10K).
export const formatMoneyCompact = (cents, always = false) => {
  const dollars = (cents || 0) / 100;
  if (!always && Math.abs(dollars) < 10000) return DOLLARS.format(Math.round(dollars));
  try {
    return currency({ notation: 'compact', maximumFractionDigits: 1 }).format(dollars);
  } catch {
    return DOLLARS.format(Math.round(dollars));
  }
};

// '$1,234.5' -> 123450, or null when the text isn't an amount of money. Parsed
// as text rather than through parseFloat, so float error can never shave a cent.
export const parseDollars = (text) => {
  const cleaned = String(text ?? '').replace(/[$,\s]/g, '');
  const match = /^(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (!match || !/\d/.test(cleaned)) return null;
  return Number(match[1] || 0) * 100 + Number((match[2] || '').padEnd(2, '0'));
};

// Storage is metered in binary units, as the API bills it.
const MB = 1024 ** 2;
const GB = 1024 ** 3;
const TB = 1024 ** 4;

export const formatStorage = (bytes) => {
  const n = Number(bytes || 0);
  if (n >= TB) return `${(n / TB).toFixed(1)} TB`;
  if (n >= GB) return `${n / GB >= 100 ? Math.round(n / GB) : (n / GB).toFixed(1)} GB`;
  if (n >= MB) return `${Math.round(n / MB)} MB`;
  return n > 0 ? '< 1 MB' : '0 GB';
};

export const plural = (count, word, many = `${word}s`) =>
  `${Number(count || 0).toLocaleString()} ${count === 1 ? word : many}`;

// --- months ------------------------------------------------------------------
// Months travel as 'YYYY-MM'. new Date('2026-09') parses as UTC midnight, which
// is still August in the Americas, so these build dates from the parts.

export const monthDate = (key) => {
  const [year, month] = String(key).split('-').map(Number);
  return new Date(year, (month || 1) - 1, 1);
};

export const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const currentMonthKey = () => monthKey(new Date());

export const addMonths = (key, count) => {
  const date = monthDate(key);
  return monthKey(new Date(date.getFullYear(), date.getMonth() + count, 1));
};

export const monthsBetween = (start, end) => {
  const months = [];
  for (let key = start; key <= end; key = addMonths(key, 1)) months.push(key);
  return months;
};

export const formatMonth = (key, options = { month: 'short', year: 'numeric' }) =>
  monthDate(key).toLocaleDateString(undefined, options);

export const formatMonthLong = (key) => formatMonth(key, { month: 'long', year: 'numeric' });

// Axis labels name the year only where it changes: "Nov '25", "Dec", "Jan '26".
export const monthAxisLabel = (key, index) => {
  const date = monthDate(key);
  const short = date.toLocaleDateString(undefined, { month: 'short' });
  return index === 0 || date.getMonth() === 0 ? `${short} '${String(date.getFullYear()).slice(2)}` : short;
};

// --- invoice lines -------------------------------------------------------------

const UNIT_PLURALS = { seat: 'seats', submission: 'submissions', 'guest submission': 'guest submissions' };

// "42 seats × $35.00". The platform fee is a flat monthly charge, so it just
// says so rather than "1 month × $500.00".
export const describeLine = (line) => {
  if (line.unit === 'month') return 'Flat monthly fee';
  const unit = line.quantity === 1 ? line.unit : UNIT_PLURALS[line.unit] || line.unit;
  return `${Number(line.quantity).toLocaleString()} ${unit} × ${formatMoney(line.unit_cents)}`;
};

export const PAYMENT_METHODS = [
  { id: 'card', label: 'Card' },
  { id: 'ach', label: 'ACH transfer' },
  { id: 'check', label: 'Check' },
  { id: 'wire', label: 'Wire' },
  { id: 'other', label: 'Other' },
];

export const paymentMethodLabel = (id) => PAYMENT_METHODS.find((m) => m.id === id)?.label || id;
